// Who is asking. Built by the adapters from JWT claims or the authorizer context, never by
// hand in a handler. `tenantId` is the tenant this request acts in; the control plane's own
// staff act in the literal tenant `platform`.
import { z } from 'zod';
import { ROLES, TIERS } from './roles.js';
/** The control plane's own tenant id. Platform staff act here; platform resources live here. */
export const PLATFORM_TENANT = 'platform';
export const GRANT_PATTERN = /^(office|tenant)#[^\s#]+$/;
export const grantSchema = z.string().regex(GRANT_PATTERN);
export const membershipSchema = z.object({
    tenantId: z.string().min(1),
    roles: z.array(z.enum(ROLES)),
    officeCode: z.string().min(1).optional(),
    agentCode: z.string().min(1).optional(),
    grants: z.array(grantSchema).optional(),
});
/** Wire form of a principal (grants as an array). `toPrincipal` turns it into the runtime shape. */
export const principalWireSchema = z.object({
    sub: z.string().min(1),
    tenantId: z.string().min(1).optional(),
    tier: z.enum(TIERS),
    memberships: z.array(membershipSchema),
    roles: z.array(z.enum(ROLES)),
    officeCode: z.string().min(1).optional(),
    agentCode: z.string().min(1).optional(),
    grants: z.array(grantSchema),
});
export function toPrincipal(wire) {
    return {
        sub: wire.sub,
        ...(wire.tenantId !== undefined ? { tenantId: wire.tenantId } : {}),
        tier: wire.tier,
        memberships: wire.memberships,
        roles: wire.roles,
        ...(wire.officeCode !== undefined ? { officeCode: wire.officeCode } : {}),
        ...(wire.agentCode !== undefined ? { agentCode: wire.agentCode } : {}),
        grants: new Set(wire.grants),
    };
}
export function toWire(principal) {
    return {
        sub: principal.sub,
        ...(principal.tenantId !== undefined ? { tenantId: principal.tenantId } : {}),
        tier: principal.tier,
        memberships: [...principal.memberships],
        roles: [...principal.roles],
        ...(principal.officeCode !== undefined ? { officeCode: principal.officeCode } : {}),
        ...(principal.agentCode !== undefined ? { agentCode: principal.agentCode } : {}),
        grants: [...principal.grants],
    };
}
export function officeGrant(officeCode) {
    return `office#${officeCode}`;
}
export function tenantGrant(tenantId) {
    return `tenant#${tenantId}`;
}
/** Office codes the principal holds an explicit grant on. */
export function grantedOffices(principal) {
    const offices = [];
    for (const grant of principal.grants) {
        if (grant.startsWith('office#'))
            offices.push(grant.slice('office#'.length));
    }
    return offices;
}
export function hasRole(principal, ...roles) {
    return roles.some((r) => principal.roles.includes(r));
}
/** Platform staff: the PlatformAdmin role held while acting in the `platform` tenant. */
export function isPlatformAdmin(principal) {
    return principal.tenantId === PLATFORM_TENANT && principal.roles.includes('PlatformAdmin');
}
//# sourceMappingURL=principal.js.map